/**
 * Privacy Consent Store (US Opt-Out Model)
 *
 * Central client-side state for CCPA/CPRA-style opt-out consent. Consumed by
 * the `privacy-consent` block (UI) and by tracker blocks via
 * `useTrackingAllowed()` in `./analytics.ts`.
 *
 * Design notes:
 *
 * - US laws (CA/CO/TX/VA/CT/OR/…) are opt-*out* regimes: trackers can load by
 *   default. We disable them once the visitor opts out through the block UI
 *   or if the browser sends the Global Privacy Control (GPC) signal
 *   (`navigator.globalPrivacyControl === true`). Honoring GPC is legally
 *   required in several states — it is intentionally not user-configurable.
 *
 * - Two tracking categories: `advertising` (Meta pixel, Google Ads, GTM,
 *   TikTok, AdTaxi, Basis, OpenAI, image pixels, hy.ly) and `analytics` (GA4,
 *   Clarity, CallRail). GPC and the `advertising` scope only disable the
 *   first bucket; a site can opt for `all` to gate both (block config).
 *
 * - Persistence uses a first-party cookie so the flag survives across
 *   subdomains a property might use, mirrored to localStorage as a fallback
 *   (some browsers or privacy modes suppress third-party-esque cookies).
 *
 * - This is a plain external store (subscribe/getSnapshot) so it can be
 *   consumed via `useSyncExternalStore`. React trees mount long after
 *   `window.load`, and effects re-run when the store changes, so gating
 *   is reactive — no page reload required to enforce a new opt-out.
 */

const COOKIE_NAME = 'vibesites_privacy_optout';
const STORAGE_KEY = 'vibesites:privacy:optout';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

export type TrackingCategory = 'advertising' | 'analytics';

export type OptOutSource = 'user' | 'gpc' | null;

export interface ConsentState {
  /** True when tracking should be gated for `categoriesGated`. */
  optedOut: boolean;
  /** Which mechanism set the current opt-out (null when not opted out). */
  source: OptOutSource;
  /**
   * Categories the opt-out applies to. Configured by the privacy-consent
   * block via `setCategoriesGated`. Default is `['advertising']` — that's
   * what GPC covers on its own, and it's the legally load-bearing bucket.
   */
  categoriesGated: readonly TrackingCategory[];
}

const DEFAULT_STATE: ConsentState = {
  optedOut: false,
  source: null,
  categoriesGated: ['advertising'],
};

let state: ConsentState = { ...DEFAULT_STATE };
const listeners = new Set<() => void>();
let initialized = false;

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

function readCookieOptOut(): boolean {
  if (typeof document === 'undefined') return false;
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${COOKIE_NAME}=`));
  return match?.split('=')[1] === '1';
}

function writeCookieOptOut(optedOut: boolean): void {
  if (typeof document === 'undefined') return;
  if (optedOut) {
    document.cookie = `${COOKIE_NAME}=1; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
    return;
  }
  document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
}

function readStorageOptOut(): boolean {
  try {
    return window.localStorage?.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeStorageOptOut(optedOut: boolean): void {
  try {
    if (optedOut) {
      window.localStorage?.setItem(STORAGE_KEY, '1');
      return;
    }
    window.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // Storage disabled — cookie is the authoritative fallback.
  }
}

function detectGpc(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

function emit(): void {
  for (const l of listeners) l();
}

function initIfNeeded(): void {
  if (initialized) return;
  initialized = true;
  if (typeof window === 'undefined') return;

  if (detectGpc()) {
    state = { ...state, optedOut: true, source: 'gpc' };
    return;
  }

  if (readCookieOptOut() || readStorageOptOut()) {
    state = { ...state, optedOut: true, source: 'user' };
  }
}

export function getConsentState(): ConsentState {
  initIfNeeded();
  return state;
}

export function subscribeConsent(listener: () => void): () => void {
  initIfNeeded();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Set the categories that the opt-out flag gates. Called by the
 * `privacy-consent` block from its `opt_out_scope` token so a site can
 * choose between disabling only advertising (default, matches GPC scope)
 * or all tracking including first-party analytics.
 */
export function setCategoriesGated(categories: readonly TrackingCategory[]): void {
  initIfNeeded();
  // Skip if the list is identical to avoid triggering re-renders.
  const same =
    categories.length === state.categoriesGated.length &&
    categories.every((c, i) => c === state.categoriesGated[i]);
  if (same) return;
  state = { ...state, categoriesGated: categories };
  emit();
}

/**
 * Update the opt-out flag. GPC-derived opt-outs cannot be overridden by
 * user opt-in — the browser signal represents the visitor's authoritative
 * choice at the OS/browser level (Sephora settlement, CPPA guidance).
 */
export function setOptOut(optedOut: boolean): void {
  initIfNeeded();
  if (state.source === 'gpc' && !optedOut) return;
  if (state.optedOut === optedOut) return;

  state = {
    ...state,
    optedOut,
    source: optedOut ? 'user' : null,
  };

  writeCookieOptOut(optedOut);
  writeStorageOptOut(optedOut);
  applyVendorDowngrades(optedOut);
  emit();
}

// ---------------------------------------------------------------------------
// Vendor downgrades
// ---------------------------------------------------------------------------

/**
 * Best-effort downgrade of vendor SDKs that have already loaded. Reactive
 * gating in tracker blocks prevents *future* script loads and unsubscribes
 * event forwarding, but a pixel that already fired on page load can't be
 * un-fired — these vendor APIs at least mark the session as opt-out so
 * further calls apply the vendor's own restricted-processing mode.
 */
function applyVendorDowngrades(optedOut: boolean): void {
  if (!optedOut || typeof window === 'undefined') return;

  const w = window as unknown as {
    fbq?: (method: string, options: string[]) => void;
    gtag?: (command: string, options: Record<string, unknown>) => void;
    ttq?: {
      revokeConsent?: () => void;
      disableCookie?: () => void;
    };
  };

  if (typeof w.fbq === 'function') {
    try {
      // Meta Limited Data Use — flags subsequent fbq calls to be treated as
      // opt-out under CCPA. Params are country/state; empty defers to Meta's
      // geolocation. See
      // https://developers.facebook.com/docs/marketing-apis/data-processing-options
      w.fbq('dataProcessingOptions', ['LDU']);
    } catch {
      // Ignore — SDK internal errors shouldn't break the page.
    }
  }

  if (typeof w.gtag === 'function') {
    try {
      // Google's restricted_data_processing flag downgrades ad personalization
      // for the remainder of the session. See
      // https://developers.google.com/tag-platform/security/guides/restricted-data-processing
      w.gtag('set', { restricted_data_processing: true });
    } catch {
      // Ignore.
    }
  }

  if (typeof w.ttq?.revokeConsent === 'function') {
    try {
      w.ttq.revokeConsent();
    } catch {
      // Ignore.
    }
  }
  if (typeof w.ttq?.disableCookie === 'function') {
    try {
      w.ttq.disableCookie();
    } catch {
      // Ignore.
    }
  }
}
